import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { transactionAPI } from '../services/api';
import { Search, Filter, User, Calendar, MapPin, Phone, Eye, Edit } from 'lucide-react';
import { formatDate, formatCurrency } from '../utils/formatters';

const Deceased = () => {
    const navigate = useNavigate();
    const [filters, setFilters] = useState({
        page: 1,
        limit: 10,
        search: '',
    });
    const [searchInput, setSearchInput] = useState('');

    const { data, isLoading, error } = useQuery({
        queryKey: ['deceased', filters],
        queryFn: () => transactionAPI.getAll(filters),
        select: (response) => {
            // 계약 데이터에서 사망자들을 추출하여 평면화
            const deceasedList = [];
            response.data.transactions.forEach(contract => {
                if (contract.deads && Array.isArray(contract.deads)) {
                    contract.deads.forEach(dead => {
                        deceasedList.push({
                            ...dead,
                            contract_id: contract.id,
                            contract_date: contract.contract_date,
                            contract_status: contract.status,
                            contract_type: contract.type,
                            mausoleum: contract.mausoleum,
                            cost: contract.cost,
                            acquaintances: contract.acquaintances
                        });
                    });
                }
            });
            
            return {
                ...response.data.pagination,
                data: deceasedList,
                total: deceasedList.length
            };
        },
        keepPreviousData: true,
    });

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }));
    };

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    const handleQuery = () => {
        setFilters(prev => ({
            ...prev,
            search: searchInput,
            page: 1
        }));
    };

    const handleClearFilters = () => {
        setSearchInput('');
        setFilters(prev => ({
            ...prev,
            search: '',
            page: 1
        }));
    };

    useEffect(() => {
        setSearchInput(filters.search);
    }, []);

    if (isLoading) {
        return <div className="loading">사망자 정보를 불러오는 중...</div>;
    }

    if (error) {
        return (
            <div className="error">
                사망자 정보를 불러오는 중 오류가 발생했습니다: {error.message}
            </div>
        );
    }

    const getStatusColor = (status) => {
        switch (status) {
            case '완료': return 'status-completed';
            case '진행중': return 'status-progress';
            case '서류 대기': return 'status-waiting';
            default: return 'status-cancelled';
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case '신규': return 'status-progress';
            case '이장': return 'status-waiting';
            case '연장': return 'status-completed';
            default: return 'status-cancelled';
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">사망자 관리</h1>
                <div className="text-sm text-gray-500">
                    총 {data?.total || 0}명
                </div>
            </div>

            {/* 검색 필터 */}
            <div className="card mb-6">
                <div className="grid grid-3" style={{ gap: '16px', alignItems: 'end' }}>
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                            검색
                        </label>
                        <div className="relative">
                            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="사망자명, 연고자명 검색"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="input w-full pl-10"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                            페이지당 항목 수
                        </label>
                        <select
                            value={filters.limit}
                            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                            className="select w-full"
                        >
                            <option value={10}>10개</option>
                            <option value={20}>20개</option>
                            <option value={50}>50개</option>
                        </select>
                    </div>

                    <div className="flex gap-2">
                        <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
                            초기화
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleQuery}>조회</button>
                    </div>
                </div>
            </div>

            {/* 사망자 목록 테이블 */}
            {data?.data?.length > 0 ? (
                <div className="card">
                    <div className="overflow-x-auto">
                        <table className="table w-full">
                            <thead>
                                <tr>
                                    <th>묘적번호</th>
                                    <th>사망자명</th>
                                    <th>연고자명</th>
                                    <th>사망일</th>
                                    <th>매장 방법</th>
                                    <th>묘 형태</th>
                                    <th>계약일</th>
                                    <th>계약 구분</th>
                                    <th>계약 상태</th>
                                    <th>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.data.map((deceased, index) => (
                                    <tr key={`${deceased.contract_id}-${index}`}>
                                        <td className="font-medium">
                                            {deceased.mausoleum?.member_number || '정보 없음'}
                                        </td>
                                        <td>
                                            {deceased.name || '정보 없음'}
                                        </td>
                                        <td>
                                            {deceased.acquaintances?.[0]?.name || '정보 없음'}
                                        </td>
                                        <td>
                                            {formatDate(deceased.death_date)}
                                        </td>
                                        <td>
                                            {deceased.burial_method || '-'}
                                        </td>
                                        <td>
                                            {deceased.tomb_type || '-'}
                                        </td>
                                        <td>
                                            {formatDate(deceased.contract_date)}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${getTypeColor(deceased.contract_type)}`}>
                                                {deceased.contract_type}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${getStatusColor(deceased.contract_status)}`}>
                                                {deceased.contract_status}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={() => navigate(`/contracts/${deceased.contract_id}`)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="상세 보기"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => navigate(`/contracts/edit/${deceased.contract_id}`)}
                                                    className="text-green-600 hover:text-green-900"
                                                    title="수정"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* 페이지네이션 */}
                    {data?.totalPages > 1 && (
                        <div className="flex justify-between items-center mt-6">
                            <div className="text-sm text-gray-700">
                                총 {data.total}개 중 {data.from}-{data.to}개
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handlePageChange(data.currentPage - 1)}
                                    disabled={data.currentPage <= 1}
                                    className="btn btn-secondary"
                                >
                                    이전
                                </button>
                                <span className="px-3 py-2 text-sm text-gray-700">
                                    {data.currentPage} / {data.totalPages}
                                </span>
                                <button
                                    onClick={() => handlePageChange(data.currentPage + 1)}
                                    disabled={data.currentPage >= data.totalPages}
                                    className="btn btn-secondary"
                                >
                                    다음
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="empty-state">
                    <User size={48} className="text-gray-300 mb-4" />
                    <p className="text-gray-500">사망자 정보가 없습니다.</p>
                </div>
            )}
        </div>
    );
};

export default Deceased;